import type { ChartKind, WidgetChoice, WidgetSize } from '../../lib/preferences'
import { sizesFor } from '../../lib/preferences'
import type { DropZone } from './dragOrder'
import type { DashboardData, DashboardWidget } from './widgets'

export const CHART_NAMES: Record<ChartKind, string> = {
  tiles: 'Numbers',
  line: 'Line',
  area: 'Area',
  bar: 'Bars',
  stacked: 'Stacked columns',
  donut: 'Donut',
  table: 'Table'
}

export const SIZE_NAMES: Record<WidgetSize, string> = {
  full: 'Full width',
  half: 'Half',
  third: 'Third'
}

/**
 * One widget on the board, with the controls that change it attached to the thing they change.
 *
 * The strip only exists while Customise is on. An operator reading their board is not deciding how
 * wide a panel should be, and a row of selects above every chart all day is a row of selects nobody
 * is using - the arranging is occasional and the reading is constant.
 *
 * Everything here is a second call site for `updateWidget` and `moveWidget` rather than new logic.
 * The chooser list still exists and is still the only way to bring back a widget that is switched
 * off, because a hidden widget has no card to put a control on.
 */
export function WidgetCard({
  widget,
  choice,
  index,
  count,
  data,
  customising,
  dragging,
  hint,
  onMove,
  onChange,
  onDragStart
}: {
  widget: DashboardWidget
  choice: WidgetChoice
  index: number
  count: number
  data: DashboardData
  customising: boolean
  dragging: boolean
  /** Where a drop would land, when this is the card the pointer is over. */
  hint: DropZone | null
  onMove: (direction: -1 | 1) => void
  onChange: (change: Partial<Omit<WidgetChoice, 'widget'>>) => void
  onDragStart: (event: React.PointerEvent<HTMLElement>) => void
}) {
  const sizes = sizesFor(widget)

  /*
    What the card shows while something is being dragged over it.

    The two edge zones mean "put it beside me" and the middle two mean "put it before or after me",
    and they are different enough outcomes that guessing between them from a single highlight would
    be worse than no highlight at all. An edge draws a bar down that side; the middle draws one across
    the top or the bottom, which is where the panel is about to go.
  */
  const marker = hint ? ` drop-${hint}` : ''

  return (
    <div
      className={`card widget-card size-${choice.size ?? 'full'}${dragging ? ' dragging' : ''}${marker}`}
      data-widget={choice.widget}
      data-testid={`widget-${choice.widget}`}
    >
      {customising && (
        <div className="widget-bar">
          {/*
            The handle is the only thing a drag starts from, rather than the whole card. A card that
            drags from anywhere is one where selecting a row of a table, or reading a value off a
            chart, moves the panel instead.
          */}
          <span
            className="widget-grip"
            role="button"
            tabIndex={-1}
            aria-hidden="true"
            title={`Drag ${widget.title}`}
            onPointerDown={onDragStart}
          >
            ⠿
          </span>

          <span className="widget-bar-name">{widget.title}</span>

          {widget.charts.length > 1 && (
            <select
              aria-label={`How to draw ${widget.title}`}
              value={choice.chart}
              onChange={(event) => onChange({ chart: event.target.value })}
              data-testid={`card-chart-${choice.widget}`}
            >
              {widget.charts.map((kind) => (
                <option key={kind} value={kind}>
                  {CHART_NAMES[kind]}
                </option>
              ))}
            </select>
          )}

          {sizes.length > 1 && (
            <select
              aria-label={`How wide ${widget.title} is`}
              value={choice.size ?? widget.defaultSize}
              onChange={(event) => onChange({ size: event.target.value })}
              data-testid={`card-size-${choice.widget}`}
            >
              {sizes.map((size) => (
                <option key={size} value={size}>
                  {SIZE_NAMES[size]}
                </option>
              ))}
            </select>
          )}

          {/*
            The keyboard and touch path, kept beside the handle rather than replaced by it. Dragging
            is unreachable from a keyboard without a second implementation and awkward on a touch
            screen, which is why WidgetChooser refused it outright; offering both answers that
            without taking the faster gesture away from a pointer.
          */}
          <button
            type="button"
            aria-label={`Move ${widget.title} up`}
            disabled={index === 0}
            onClick={() => onMove(-1)}
            data-testid={`card-up-${choice.widget}`}
          >
            <span aria-hidden="true">↑</span>
          </button>
          <button
            type="button"
            aria-label={`Move ${widget.title} down`}
            disabled={index === count - 1}
            onClick={() => onMove(1)}
            data-testid={`card-down-${choice.widget}`}
          >
            <span aria-hidden="true">↓</span>
          </button>
          {/* Removes it from the board and puts it back in the Add menu, which is the only place it
              can be reached from once it has no card of its own. */}
          <button
            type="button"
            aria-label={`Remove ${widget.title} from the board`}
            onClick={() => onChange({ visible: false })}
            data-testid={`card-hide-${choice.widget}`}
          >
            Remove
          </button>
        </div>
      )}

      <div className="card-body">{widget.render(choice.chart as ChartKind, data)}</div>
    </div>
  )
}
